import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp.js';
import { 
  ArrowLeft,
  Bot,
  Send,
  MessageSquare,
  FileText,
  Lightbulb,
  Clock,
  Paperclip
} from 'lucide-react';

const LawyerAIAssistant = () => {
  const navigate = useNavigate();
  const { language } = useApp();
  const [message, setMessage] = useState('');
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const [chatHistory, setChatHistory] = useState([
    {
      id: 1,
      type: 'ai',
      message: 'Hello! I\'m your AI legal assistant. How can I help you today?',
      timestamp: '10:00 AM'
    },
    {
      id: 2,
      type: 'user',
      message: 'Can you help me draft a contract?',
      timestamp: '10:01 AM'
    },
    {
      id: 3,
      type: 'ai',
      message: 'Of course! I can help you draft various types of contracts. What type of contract do you need?',
      timestamp: '10:01 AM'
    }
  ]);

  const translations = {
    english: {
      aiAssistant: 'AI Assistant',
      back: 'Back',
      placeholder: 'Ask me anything about legal matters...',
      send: 'Send',
      quickActions: 'Quick Actions',
      draftContract: 'Draft Contract',
      legalResearch: 'Legal Research',
      caseAnalysis: 'Case Analysis',
      documentReview: 'Document Review'
    },
    hindi: {
      aiAssistant: 'AI सहायक',
      back: 'वापस',
      placeholder: 'कानूनी मामलों के बारे में कुछ भी पूछें...',
      send: 'भेजें',
      quickActions: 'त्वरित कार्य',
      draftContract: 'अनुबंध तैयार करें',
      legalResearch: 'कानूनी शोध',
      caseAnalysis: 'मामले का विश्लेषण',
      documentReview: 'दस्तावेज़ समीक्षा'
    }
  };

  const t = translations[language] || translations.english;

  const quickActions = [
    {
      id: 1,
      title: t.draftContract,
      icon: FileText,
      action: () => setMessage('Help me draft a contract')
    },
    {
      id: 2,
      title: t.legalResearch,
      icon: Lightbulb,
      action: () => setMessage('I need help with legal research')
    },
    {
      id: 3,
      title: t.caseAnalysis,
      icon: MessageSquare,
      action: () => setMessage('Analyze this case for me')
    },
    {
      id: 4,
      title: t.documentReview,
      icon: FileText,
      action: () => setMessage('Review this legal document')
    }
  ];

  const handleSendMessage = () => {
    if (message.trim()) {
      const newMessage = {
        id: chatHistory.length + 1,
        type: 'user',
        message: message,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setChatHistory([...chatHistory, newMessage]);
      setMessage('');
      
      // Simulate AI response
      setTimeout(() => {
        const aiResponse = {
          id: chatHistory.length + 2,
          type: 'ai',
          message: 'I understand your request. Let me help you with that. Could you provide more details?',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        setChatHistory(prev => [...prev, aiResponse]);
      }, 1000);
    }
  };

  const handleQuickAction = (action) => {
    action();
  };

  const handleAttachment = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const newMessage = {
        id: chatHistory.length + 1,
        type: 'user',
        message: `📎 ${file.name}`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        attachment: file
      };
      setChatHistory([...chatHistory, newMessage]);
      
      // Simulate AI response
      setTimeout(() => {
        const aiResponse = {
          id: chatHistory.length + 2,
          type: 'ai',
          message: 'I can see you\'ve uploaded a document. Let me analyze it for you.',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        setChatHistory(prev => [...prev, aiResponse]);
      }, 1000);
    }
  };

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/lawyer-dashboard')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {t.aiAssistant}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Your AI legal assistant
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="flex-1 px-4 py-4 space-y-4">
        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            {t.quickActions}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {quickActions.map((action) => (
              <motion.button
                key={action.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleQuickAction(action.action)}
                className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200"
              >
                <action.icon className="w-5 h-5 text-purple-500" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {action.title}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Chat Area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex-1 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <div className="h-[600px] overflow-y-auto p-4 space-y-3">
            {chatHistory.map((chat) => (
              <div
                key={chat.id}
                className={`flex ${chat.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-lg ${
                    chat.type === 'user'
                      ? 'bg-purple-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                  }`}
                >
                  <p className="text-sm leading-relaxed">{chat.message}</p>
                  <p className={`text-xs mt-1 ${
                    chat.type === 'user' ? 'text-purple-100' : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {chat.timestamp}
                  </p>
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
        </motion.div>

        {/* Message Input */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 p-3"
        >
          {/* Attachment Button */}
          <button
            onClick={handleAttachment}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <Paperclip className="w-5 h-5 text-gray-500" />
          </button>

          {/* Message Input */}
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder={t.placeholder}
            className="flex-1 px-3 py-2 bg-transparent border-none outline-none text-sm text-gray-900 dark:text-white placeholder-gray-500"
          />

          {/* Send Button */}
          <button
            onClick={handleSendMessage}
            disabled={!message.trim()}
            className="p-2 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
          >
            <Send className="w-5 h-5" />
          </button>
        </motion.div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileUpload}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
        />
      </div>
    </div>
  );
};

export default LawyerAIAssistant;